/**
 * @fileoverview Prompt template for world-element-image-prompt-creator-v2.
 *
 * A unified prompt that adapts its instructions based on the element type
 * (place, set, object, species) to create optimized image generation prompts.
 */

export const prompt = `
You are an expert prompt engineer for advanced text-to-image models.

## TASK INSTRUCTIONS:
Your task is to take the provided world element details and create a single, powerful, and artistic prompt that can be fed into an image generation model like Nano Banana.

- **Synthesize Details:** Combine all the provided element information into a coherent visual concept. Focus the prompt on the most visually distinctive and evocative aspects of the element.
- **Adapt to Element Type:** Tailor the prompt based on the type of element:
{{#if (eq elementType "place")}}
  - This is a **place** ({{elementSubtype}}). Create an establishing shot or landscape view that captures the geography, architecture, atmosphere, and scale of the location. Include environmental details like weather, lighting, and time of day.
{{/if}}
{{#if (eq elementType "set")}}
  - This is a **set** ({{elementSubtype}}). Create a cinematic, production-design view that captures the staged environment with its lighting, atmosphere, and key objects. Think like a film director framing a scene — focus on mood, sensory details, and spatial composition.
{{/if}}
{{#if (eq elementType "object")}}
  {{#if (eq elementSubtype "outfit")}}
  - This is an **outfit**. Create a top-down flat-lay ("knolling") composition on a clean, neutral surface (e.g. wood, concrete, or plain fabric). Lay out every garment and accessory separately, with a small gap between pieces, so each one is fully visible and distinguishable — outerwear, top(s), bottom(s), shoes, bag, and accessories. **Include layered or normally-hidden pieces as their own separate items** (e.g. if the outfit has a t-shirt under a sweater, show both side by side; show underwear, socks, or undershirts as separate items too). Do **not** include a person, mannequin, or body — the outfit must read as a styled still life that could be worn by any character. Use soft, even lighting with no harsh shadows.
  {{else}}
  - This is an **object** ({{elementSubtype}}). Create a focused product-shot or artifact-study view. Place the object against an appropriate background that reflects its origin or context. Emphasize materials, craftsmanship, scale, condition, and any magical or special properties through visual cues like glow, runes, or wear.
  {{/if}}
{{/if}}
{{#if (eq elementType "species")}}
  - This is a **species** ({{elementSubtype}}). Create a naturalist illustration or creature portrait that showcases the biology, physiology, and distinctive features. Include habitat elements in the background. Capture the temperament and behavior through pose and expression.
{{/if}}
- **Be Evocative:** Use rich, descriptive language. Focus on lighting, mood, materials, textures, and atmospheric details.
- **Do Not Hallucinate:** Only use the information provided in the element details. Do not add elements that are not there unless specified in the user guidance.
- **Maintain Consistency:** If a story setting is provided, ensure the visual style reflects the era, culture, and aesthetic of that world.

Format the output in natural language and descriptive phrases. Start with the most important elements (the subject) and move to finer details and stylistic choices.
As reference, the image will be created in the resolution of 1024x1024.

<<<userMessage>>>

## WORLD ELEMENT INFORMATION

**Element Name:** {{{elementName}}}
**Element Type:** {{{elementType}}} ({{elementSubtype}})

**Element Details:**
{{#each elementDetails}}
**{{@key}}:** {{{this}}}
{{/each}}

{{#if storySetting}}
The story is set in **{{{storySetting}}}**.
Your prompt should be tailored to the culture, architecture, technology, flora and fauna of that setting.
{{/if}}

---

## OUTPUT INSTRUCTIONS:
Provide your output in the required JSON format. The output must be a single string.

`;
